/** Column definitions for the delivery-logs grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget (the page also sets `disableColumnFilter`). Only
 * created/finalized are sortable (the server's ordering whitelist). Columns
 * hidden by default (see DeliveryLogsPage's DEFAULT_HIDDEN) are controlled via
 * the presets hook's column-visibility model, NOT a column-level `hide` prop.
 */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Link as RouterLink } from 'react-router-dom';

import type { DeliveryLogListItem } from '@/api/eventDeliveryLogs';
import { CellActionsPopover } from '@/components/DataGrid/CellActionsPopover';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { formatDate } from '@/utils/format';

import {
  capturedCount,
  EVENT_STATUS,
  eventStatusTone,
  isSandboxEventStatus,
  methodStatusTone,
  sandboxMethodLabel,
  type StatusTone,
} from './deliveryStatus';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

/** Transient — the recovery cron moves every orphan to 10/11/12. */
export const STATUS_ORPHANED = EVENT_STATUS.ORPHANED;

const CHIP_COLOR: Record<StatusTone, 'success' | 'error' | 'warning' | 'default'> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  neutral: 'default',
};

/** A sandbox outcome's mark: the neutral chip carries the flask, so "Captured (sandbox)" never
 *  reads as an in-flight state like Emitted. */
const sandboxIcon = <ScienceOutlinedIcon sx={{ fontSize: 14 }} />;

/** An event's delivery status (Event_Delivery_Log.STATUSES). */
export function DeliveryStatusChip({ status, label }: { status: number; label: string }) {
  const tone = eventStatusTone(status);
  return (
    <Chip
      size="small"
      color={CHIP_COLOR[tone]}
      variant={tone === 'neutral' ? 'outlined' : 'filled'}
      label={label}
      {...(isSandboxEventStatus(status) && { icon: sandboxIcon })}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** One delivery method's status (Event_Delivery_Notification_Log.STATUSES). */
export function MethodStatusChip({ status, label }: { status: number; label: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={CHIP_COLOR[methodStatusTone(status)]}
      label={label}
      {...(sandboxMethodLabel(status) && { icon: sandboxIcon })}
      sx={{ height: 20, fontSize: 11 }}
    />
  );
}

/** Per-notification counters: "2 ok · 1 err of 3". A sandbox's row adds a neutral second
 *  line, "3 captured" (n_captured is 0 on every production row, which reads as before). */
function ResultsCell({ row }: { row: DeliveryLogListItem }) {
  if (row.n_notifications === 0) {
    return <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>;
  }
  const captured = capturedCount(row.n_captured);
  const counts = (
    <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      <Box component="span" sx={{ color: row.n_success > 0 ? 'success.main' : 'text.disabled' }}>
        {row.n_success} ok
      </Box>
      {' · '}
      <Box component="span" sx={{ color: row.n_error > 0 ? 'error.main' : 'text.disabled' }}>
        {row.n_error} err
      </Box>
      <Box component="span" sx={{ color: 'text.secondary' }}> of {row.n_notifications}</Box>
    </Typography>
  );
  if (!captured) return counts;
  return (
    <Stack spacing={0} sx={{ py: 0.75 }}>
      {counts}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {captured}
      </Typography>
    </Stack>
  );
}

/** log_id with the free-text log_tag on a muted second line. */
function LogIdCell({ row }: { row: DeliveryLogListItem }) {
  return (
    <Stack spacing={0} sx={{ py: 0.75, minWidth: 0 }}>
      <Box component="span" sx={{ ...mono, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {row.log_id}
      </Box>
      {row.log_tag && (
        <Typography component="span" variant="caption" color="text.secondary" noWrap title={row.log_tag}>
          {row.log_tag}
        </Typography>
      )}
    </Stack>
  );
}

function TsCell({ iso }: { iso: string | null }) {
  if (!iso) return <>—</>;
  return (
    <Tooltip title={iso} placement="top" arrow>
      <Box component="span">{formatDate(iso)}</Box>
    </Tooltip>
  );
}

export interface BuildColumnsOptions {
  /** The Tenant column only exists when no tenant is picked above. */
  showTenant: boolean;
}

export function buildColumns({ showTenant }: BuildColumnsOptions): GridColDef<DeliveryLogListItem>[] {
  const cols: GridColDef<DeliveryLogListItem>[] = [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (p) => (
        <Tooltip title="Open delivery log">
          <IconButton
            size="small"
            component={RouterLink}
            to={`/delivery-logs/${p.row.id}`}
            aria-label="Open delivery log"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'created_ts',
      headerName: 'Emitted',
      width: 170,
      filterable: false,
      renderCell: (p) => <TsCell iso={p.row.created_ts} />,
    },
    {
      field: 'event',
      headerName: 'Event',
      flex: 1,
      minWidth: 190,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.event}</Box>,
    },
    {
      field: 'log_id',
      headerName: 'Log ID / tag',
      flex: 1,
      minWidth: 170,
      filterable: false,
      sortable: false,
      renderCell: (p) => <LogIdCell row={p.row} />,
    },
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 140,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.tour_operator_code ?? '—',
      renderCell: (p) =>
        p.row.tour_operator_id != null ? (
          <WithTenantMark environment={p.row.tour_operator_environment} placement="under">
            <CellActionsPopover
              auto
              actions={[
                {
                  key: 'open',
                  label: 'Open tour operator',
                  icon: <OpenInNewIcon fontSize="small" />,
                  href: `/tour-operators/${p.row.tour_operator_id}`,
                },
              ]}
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {p.row.tour_operator_code ?? `#${p.row.tour_operator_id}`}
            </CellActionsPopover>
          </WithTenantMark>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    });
  }

  cols.push(
    {
      field: 'status',
      headerName: 'Status',
      // Fits the longest labels whole: "Orphaned and unrecoverable", "Partially sent (allowlisted)".
      width: 210,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) => row.status_display,
      renderCell: (p) => <DeliveryStatusChip status={p.row.status} label={p.row.status_display} />,
    },
    {
      field: 'results',
      headerName: 'Results',
      width: 140,
      filterable: false,
      sortable: false,
      valueGetter: (_v, row) =>
        [`${row.n_success}/${row.n_error}/${row.n_notifications}`, capturedCount(row.n_captured)]
          .filter(Boolean)
          .join(' · '),
      renderCell: (p) => <ResultsCell row={p.row} />,
    },
    {
      field: 'user_email',
      headerName: 'Recipient',
      flex: 1,
      minWidth: 180,
      filterable: false,
      sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.row.user_email || '—'}
        </Box>
      ),
    },
    {
      field: 'error_series_id',
      headerName: 'Error series',
      width: 130,
      filterable: false,
      sortable: false,
      renderCell: (p) =>
        p.row.error_series_id ? (
          <Box component="span" sx={mono}>{p.row.error_series_id}</Box>
        ) : (
          <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
        ),
    },
    {
      field: 'order_ref',
      headerName: 'Order #',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.order_ref || '—'}</Box>,
    },
    {
      field: 'booking_id',
      headerName: 'Booking ID',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.booking_id || '—'}</Box>,
    },
    {
      field: 'duration_ms',
      headerName: 'Duration',
      width: 110,
      filterable: false,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_v, row) => row.duration_ms,
      renderCell: (p) => (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {p.row.duration_ms} ms
        </Box>
      ),
    },
    {
      field: 'finalized_ts',
      headerName: 'Finalized',
      width: 170,
      filterable: false,
      renderCell: (p) => <TsCell iso={p.row.finalized_ts} />,
    },
    {
      field: 'supplier_ref',
      headerName: 'Supplier ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.supplier_ref || '—'}</Box>,
    },
    {
      field: 'reseller_ref',
      headerName: 'Reseller ref',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.reseller_ref || '—'}</Box>,
    },
    {
      field: 'request_id',
      headerName: 'Request ID',
      width: 160,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.request_id || '—'}</Box>,
    },
    {
      field: 'tour_id',
      headerName: 'Tour ID',
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.tour_id ?? '—'}</Box>,
    },
    {
      field: 'participant_id',
      headerName: 'Participant ID',
      width: 120,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span" sx={mono}>{p.row.participant_id ?? '—'}</Box>,
    },
    {
      // Not in the server ordering whitelist (created/finalized only) — display-only.
      field: 'departure',
      headerName: 'Departure',
      width: 140,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span">{p.row.departure || '—'}</Box>,
    },
    {
      field: 'trip_start_ts',
      headerName: 'Trip start',
      width: 170,
      filterable: false,
      sortable: false,
      renderCell: (p) => <TsCell iso={p.row.trip_start_ts} />,
    },
    {
      field: 'rendering_lang',
      headerName: 'Lang',
      width: 80,
      filterable: false,
      sortable: false,
      renderCell: (p) => <Box component="span">{p.row.rendering_lang || '—'}</Box>,
    },
  );

  return cols;
}
